import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { useAuth } from "@/lib/auth";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { RiskBadge, StatusPill } from "@/components/RiskBadge";
import ObservationsToolbar from "@/components/ObservationsToolbar";
import { CalendarBlank, ArrowSquareOut } from "@phosphor-icons/react";

export default function ClientObservations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cycles, setCycles] = useState([]);
  const [cycleId, setCycleId] = useState("");
  const [observations, setObservations] = useState([]);

  const loadObs = useCallback(async (cid) => {
    const url = cid ? `/observations?cycle_id=${cid}` : `/observations?company_id=${user.company_id}`;
    const r = await api.get(url);
    setObservations(r.data);
  }, [user?.company_id]);

  useEffect(() => {
    (async () => {
      if (!user?.company_id) return;
      const c = await api.get(`/cycles?company_id=${user.company_id}`);
      setCycles(c.data);
      if (c.data.length) {
        const last = c.data[c.data.length - 1].id;
        setCycleId(last);
        loadObs(last);
      } else {
        loadObs(null);
      }
    })();
  }, [user?.company_id, loadObs]);

  const onCycleChange = (v) => {
    setCycleId(v);
    loadObs(v === "ALL" ? null : v);
  };

  const header = (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Geist" }}>Observations</h1>
        <p className="text-sm text-slate-500 mt-0.5">Click a row or card to open the discussion thread, attach evidence and submit your resolution.</p>
      </div>
      <Select value={cycleId || "ALL"} onValueChange={onCycleChange}>
        <SelectTrigger className="w-[200px]" data-testid="client-cycle-select">
          <CalendarBlank size={14} className="mr-1" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">All Cycles</SelectItem>
          {cycles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <AppLayout header={header}>
      <ObservationsToolbar observations={observations} role="client">
        {(filtered) => (
          <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Sr.</th>
                    <th className="px-3 py-2 text-left">Risk</th>
                    <th className="px-3 py-2 text-left">Area</th>
                    <th className="px-3 py-2 text-left">Observation</th>
                    <th className="px-3 py-2 text-left">Impact</th>
                    <th className="px-3 py-2 text-left">Recommendation</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={8} className="py-12 text-center text-sm text-slate-500" data-testid="client-no-obs">No observations match the current filters.</td></tr>
                  )}
                  {filtered.map((o) => (
                    <tr
                      key={o.id}
                      className="border-t border-slate-100 hover:bg-slate-50/60 align-top cursor-pointer"
                      data-testid={`client-obs-row-${o.id}`}
                      onClick={(e) => {
                        if (e.target.closest("button, a")) return;
                        navigate(`/client/observations/${o.id}`);
                      }}
                    >
                      <td className="px-3 py-3 tabular-nums">{o.sr_no}{o.rolled_forward_from && <div className="text-[9px] uppercase tracking-wider text-blue-600">Rolled fwd</div>}</td>
                      <td className="px-3 py-3"><RiskBadge risk={o.risk} /></td>
                      <td className="px-3 py-3 font-medium text-slate-800">{o.area}</td>
                      <td className="px-3 py-3 max-w-[280px] text-slate-700 whitespace-pre-line">{o.observation}</td>
                      <td className="px-3 py-3 max-w-[220px] text-slate-600 whitespace-pre-line">{o.impact}</td>
                      <td className="px-3 py-3 max-w-[220px] text-slate-600 whitespace-pre-line">{o.recommendation}</td>
                      <td className="px-3 py-3"><StatusPill status={o.status} /></td>
                      <td className="px-3 py-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/client/observations/${o.id}`)} data-testid={`open-client-detail-${o.id}`}>
                          <ArrowSquareOut size={14} className="mr-1" /> Open
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </ObservationsToolbar>
    </AppLayout>
  );
}
